import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Three-phase right-page flip state machine for the highlight deck.
 *
 *   Phase 1 — 'flipping'        rotateY 0° → -92°   PHASE1_MS  (ease-in)
 *   Phase 2 — 'resetting'       snap to +92°        0ms        (no transition)
 *   Phase 3 — 'flipping-back'   rotateY +92° → 0°   PHASE3_MS  (ease-out)
 *
 * `idle` is the resting state between flips.
 *
 * Why setTimeout + double-rAF (not transitionend):
 *   transitionend fires for the wrong property (box-shadow races with
 *   transform), fires late, and is unreliable when rapid clicks queue. The
 *   explicit timers guarantee every click has the same total duration. The
 *   double-rAF between phase 2 and phase 3 is what forces the +92° snap to
 *   paint before the back-transition starts — without it Chromium collapses
 *   phases 2+3 into a single transition ~30% of the time, producing the
 *   fold-and-unfold motion that was ripped out in R3.2.
 *
 * Keep PHASE1_MS / PHASE3_MS in sync with the CSS transition durations in
 * HighlightDeck.css (`.psl-hl-deck.is-flipping .psl-hl` / `.is-flipping-back`).
 */

export type FlipPhase = 'idle' | 'flipping' | 'resetting' | 'flipping-back';

const PHASE1_MS = 520;
const PHASE3_MS = 580;
const TAIL_BUFFER = 40;

interface UseDeckFlipOptions {
  count: number;
  initial?: number;
  prefersReducedMotion?: boolean;
}

interface UseDeckFlipResult {
  index: number;
  phase: FlipPhase;
  goTo: (target: number) => void;
  next: () => void;
  prev: () => void;
  inFlight: boolean;
}

export function useDeckFlip({
  count,
  initial = 0,
  prefersReducedMotion = false,
}: UseDeckFlipOptions): UseDeckFlipResult {
  const safeInitial = Math.max(0, Math.min(initial, Math.max(0, count - 1)));
  const [index, setIndex] = useState(safeInitial);
  const [phase, setPhase] = useState<FlipPhase>('idle');

  // Refs avoid re-rendering while the choreography is mid-flight, and let
  // the next click read the live state without waiting for React to flush.
  const inFlightRef = useRef(false);
  const indexRef = useRef(safeInitial);
  const reducedRef = useRef(prefersReducedMotion);

  // Outstanding timer/raf handles so we can clear on unmount or rapid replay.
  const timersRef = useRef<{
    phase2?: ReturnType<typeof setTimeout>;
    phase3?: ReturnType<typeof setTimeout>;
    safety?: ReturnType<typeof setTimeout>;
    raf1?: number;
    raf2?: number;
  }>({});

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    reducedRef.current = prefersReducedMotion;
  }, [prefersReducedMotion]);

  const clearPending = useCallback(() => {
    const t = timersRef.current;
    if (t.phase2) clearTimeout(t.phase2);
    if (t.phase3) clearTimeout(t.phase3);
    if (t.safety) clearTimeout(t.safety);
    if (t.raf1) cancelAnimationFrame(t.raf1);
    if (t.raf2) cancelAnimationFrame(t.raf2);
    timersRef.current = {};
  }, []);

  useEffect(() => () => clearPending(), [clearPending]);

  // Guard against `count` shrinking under us (e.g. an entry expiring mid-session).
  useEffect(() => {
    if (count === 0) return;
    if (indexRef.current > count - 1) {
      clearPending();
      const safe = count - 1;
      indexRef.current = safe;
      inFlightRef.current = false;
      setIndex(safe);
      setPhase('idle');
    }
  }, [count, clearPending]);

  const goTo = useCallback(
    (target: number) => {
      if (count === 0) return;
      const clamped = ((target % count) + count) % count;
      if (inFlightRef.current) return;
      if (clamped === indexRef.current) return;

      // Reduced-motion: swap content instantly, skip the phase machinery.
      if (reducedRef.current) {
        indexRef.current = clamped;
        setIndex(clamped);
        setPhase('idle');
        return;
      }

      inFlightRef.current = true;
      setPhase('flipping');

      // Phase 1 ends — page is edge-on. Swap content + snap to the other side.
      timersRef.current.phase2 = setTimeout(() => {
        indexRef.current = clamped;
        setIndex(clamped);
        setPhase('resetting');

        // Double-rAF: first frame commits the +92° snap to layout; second
        // frame waits until that snap has painted before we start phase 3.
        timersRef.current.raf1 = requestAnimationFrame(() => {
          timersRef.current.raf2 = requestAnimationFrame(() => {
            setPhase('flipping-back');

            timersRef.current.phase3 = setTimeout(() => {
              setPhase('idle');
              inFlightRef.current = false;
            }, PHASE3_MS + TAIL_BUFFER);
          });
        });
      }, PHASE1_MS + TAIL_BUFFER);

      // Safety net — never leave the stage stuck if rAF/timers are throttled
      // (background tab, sleeping device). Caps total flight time.
      timersRef.current.safety = setTimeout(() => {
        if (inFlightRef.current) {
          indexRef.current = clamped;
          setIndex(clamped);
          setPhase('idle');
          inFlightRef.current = false;
        }
      }, PHASE1_MS + PHASE3_MS + 400);
    },
    [count],
  );

  const next = useCallback(() => {
    if (count === 0) return;
    goTo(((indexRef.current + 1) % count + count) % count);
  }, [count, goTo]);

  const prev = useCallback(() => {
    if (count === 0) return;
    goTo(((indexRef.current - 1) % count + count) % count);
  }, [count, goTo]);

  return {
    index,
    phase,
    goTo,
    next,
    prev,
    inFlight: phase !== 'idle',
  };
}
